/**
 * RegEx Tester Service
 * Tests regular expressions with various flags and provides match details
 */

import { EncodingOperation, EncoderToolType } from '../types/EncodingOperation';
import { ValidationError, ProcessingError } from '../utils/errorHandler';

export interface RegexFlags {
  global?: boolean;
  multiline?: boolean;
  caseInsensitive?: boolean;
  unicode?: boolean;
  sticky?: boolean;
  dotAll?: boolean;
}

export interface RegexMatch {
  match: string;
  index: number;
  groups: string[];
  namedGroups?: Record<string, string>;
}

export interface RegexResult {
  matches: RegexMatch[];
  matchCount: number;
  captureGroupCount: number;
  hasMatches: boolean;
}

export interface ExamplePattern {
  name: string;
  pattern: string;
  flags: string;
  description: string;
  testString?: string;
}

export class RegexTesterService {
  private static operationCounter = 0;

  /**
   * Test a regular expression against input text
   */
  static async test(
    pattern: string,
    testString: string,
    flags: RegexFlags = {}
  ): Promise<EncodingOperation> {
    const operationId = `regex-tester-${Date.now()}-${++this.operationCounter}`;

    try {
      // Validate inputs
      this.validateInput(pattern, testString);

      // Build flags string
      const flagsString = this.buildFlagsString(flags);

      // Create regex
      let regex: RegExp;
      try {
        regex = new RegExp(pattern, flagsString);
      } catch (error) {
        throw new ValidationError(
          `Invalid regular expression: ${error instanceof Error ? error.message : 'Pattern error'}`,
          EncoderToolType.REGEX_TESTER
        );
      }

      // Execute regex
      const result = this.executeRegex(regex, testString);

      const outputJson = JSON.stringify(result, null, 2);

      return {
        id: operationId,
        toolType: EncoderToolType.REGEX_TESTER,
        operationType: 'encode',
        inputData: testString,
        outputData: outputJson,
        timestamp: new Date(),
        status: 'success',
        metadata: {
          pattern,
          flags: flagsString,
          matchCount: result.matchCount,
          captureGroupCount: result.captureGroupCount,
          hasMatches: result.hasMatches,
          inputLength: testString.length,
          outputLength: outputJson.length
        }
      };
    } catch (error) {
      if (error instanceof ValidationError) {
        throw error;
      }

      throw new ProcessingError(
        error instanceof Error ? error.message : 'Regex test failed',
        EncoderToolType.REGEX_TESTER
      );
    }
  }

  /**
   * Execute regex and collect all matches
   */
  private static executeRegex(regex: RegExp, text: string): RegexResult {
    const matches: RegexMatch[] = [];
    let captureGroupCount = 0;

    if (regex.global) {
      // Global flag: use matchAll
      const matchIterator = text.matchAll(regex);
      for (const match of matchIterator) {
        if (match.index !== undefined) {
          const groups = match.slice(1).map(g => g ?? '');
          if (groups.length > captureGroupCount) {
            captureGroupCount = groups.length;
          }

          matches.push({
            match: match[0],
            index: match.index,
            groups,
            namedGroups: match.groups
          });
        }
      }
    } else {
      // Non-global: single match
      const match = regex.exec(text);
      if (match && match.index !== undefined) {
        const groups = match.slice(1).map(g => g ?? '');
        captureGroupCount = groups.length;

        matches.push({
          match: match[0],
          index: match.index,
          groups,
          namedGroups: match.groups
        });
      }
    }

    return {
      matches,
      matchCount: matches.length,
      captureGroupCount,
      hasMatches: matches.length > 0
    };
  }

  /**
   * Build flags string from flags object
   */
  private static buildFlagsString(flags: RegexFlags): string {
    let flagsString = '';
    if (flags.global) flagsString += 'g';
    if (flags.multiline) flagsString += 'm';
    if (flags.caseInsensitive) flagsString += 'i';
    if (flags.unicode) flagsString += 'u';
    if (flags.sticky) flagsString += 'y';
    if (flags.dotAll) flagsString += 's';
    return flagsString;
  }

  /**
   * Validate input parameters
   */
  private static validateInput(pattern: string, testString: string): void {
    if (!pattern) {
      throw new ValidationError('Regular expression pattern cannot be empty', EncoderToolType.REGEX_TESTER);
    }

    if (testString === null || testString === undefined) {
      throw new ValidationError('Test string cannot be null or undefined', EncoderToolType.REGEX_TESTER);
    }

    // Check size limits
    const maxPatternLength = 10000;
    const maxTextLength = 1024 * 1024; // 1MB

    if (pattern.length > maxPatternLength) {
      throw new ValidationError(
        `Pattern exceeds maximum length of ${maxPatternLength} characters`,
        EncoderToolType.REGEX_TESTER
      );
    }

    if (testString.length > maxTextLength) {
      throw new ValidationError(
        `Test string exceeds maximum length of ${maxTextLength / 1024}KB`,
        EncoderToolType.REGEX_TESTER
      );
    }
  }

  /**
   * Get example regex patterns
   */
  static getExamplePatterns(): ExamplePattern[] {
    return [
      {
        name: 'Email Address',
        pattern: '[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}',
        flags: 'g',
        description: 'Match email addresses',
        testString: 'Contact us at support@example.com or sales@company.org'
      },
      {
        name: 'URL',
        pattern: 'https?://[^\\s]+',
        flags: 'g',
        description: 'Match HTTP/HTTPS URLs',
        testString: 'Visit https://example.com or http://test.org for more info'
      },
      {
        name: 'Phone Number (US)',
        pattern: '\\(?\\d{3}\\)?[-\\s]?\\d{3}[-\\s]?\\d{4}',
        flags: 'g',
        description: 'Match US phone numbers',
        testString: 'Call (555) 123-4567 or 555-987-6543'
      },
      {
        name: 'IP Address',
        pattern: '\\b(?:\\d{1,3}\\.){3}\\d{1,3}\\b',
        flags: 'g',
        description: 'Match IPv4 addresses',
        testString: 'Servers at 192.168.1.1 and 10.0.0.1'
      },
      {
        name: 'Hex Color',
        pattern: '#[0-9a-fA-F]{6}\\b|#[0-9a-fA-F]{3}\\b',
        flags: 'g',
        description: 'Match hex color codes',
        testString: 'Colors: #FF5733, #3498db, #abc'
      },
      {
        name: 'Date (YYYY-MM-DD)',
        pattern: '\\d{4}-\\d{2}-\\d{2}',
        flags: 'g',
        description: 'Match ISO date format',
        testString: 'Events: 2024-01-15 and 2024-12-31'
      },
      {
        name: 'Time (HH:MM)',
        pattern: '\\b([01]?\\d|2[0-3]):([0-5]\\d)\\b',
        flags: 'g',
        description: 'Match 24-hour time format with capture groups',
        testString: 'Meeting at 09:30 and 14:45'
      },
      {
        name: 'Word Boundaries',
        pattern: '\\btest\\b',
        flags: 'gi',
        description: 'Match whole word "test" (case-insensitive)',
        testString: 'This is a test. Testing is important. Contest starts now.'
      },
      {
        name: 'Capture Groups',
        pattern: '(\\w+)@(\\w+)\\.(\\w+)',
        flags: 'g',
        description: 'Capture email parts',
        testString: 'admin@example.com'
      },
      {
        name: 'Named Groups',
        pattern: '(?<username>\\w+)@(?<domain>\\w+\\.\\w+)',
        flags: 'g',
        description: 'Named capture groups for email',
        testString: 'user@example.com'
      },
      {
        name: 'Lookahead',
        pattern: '\\d+(?= dollars)',
        flags: 'g',
        description: 'Positive lookahead: numbers before "dollars"',
        testString: 'Price: 100 dollars and 50 cents'
      },
      {
        name: 'Lookbehind',
        pattern: '(?<=\\$)\\d+',
        flags: 'g',
        description: 'Positive lookbehind: numbers after "$"',
        testString: 'Total: $100 and $50'
      },
      {
        name: 'Multiline',
        pattern: '^\\w+',
        flags: 'gm',
        description: 'Match words at start of each line',
        testString: 'First line\nSecond line\nThird line'
      },
      {
        name: 'Dot All',
        pattern: '<div>.*?</div>',
        flags: 'gs',
        description: 'Match HTML div with content across lines',
        testString: '<div>\nContent\n</div>'
      },
      {
        name: 'Non-Greedy',
        pattern: '<.*?>',
        flags: 'g',
        description: 'Non-greedy match of HTML tags',
        testString: '<p>Paragraph</p><span>Text</span>'
      }
    ];
  }

  /**
   * Get regex flag descriptions
   */
  static getFlagDescriptions(): Record<string, string> {
    return {
      g: 'Global - Find all matches rather than stopping after the first match',
      m: 'Multiline - ^ and $ match start/end of lines, not just start/end of string',
      i: 'Case-insensitive - Match both uppercase and lowercase letters',
      u: 'Unicode - Treat pattern as a sequence of Unicode code points',
      y: 'Sticky - Match only from the index indicated by lastIndex property',
      s: 'Dot All - Dot (.) matches newline characters'
    };
  }

  /**
   * Clear cache (for testing)
   */
  static clearCache(): void {
    this.operationCounter = 0;
  }
}
